import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useCityContext } from '../../../contexts/CityProvider';
import useCitiesActions from '../../../hooks/APIHooks/users/useCitiesActions';
import { City } from '../../../types/cities';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import EditCityModal from '../../CommonComps/Modals/EditCityModal';

const CityModalContainer = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    targetedCity,
    showDeleteModal,
    showEditModal,
    setTargetedCity,
    setShowDeleteModal,
    setShowEditModal,
    setFilterProvince,
    setIsResetFilter,
    setSearchText,
  } = useCityContext();

  // integration of custom API hooks here
  const { deleteCityByIdHandler } = useCitiesActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete city feature declared here
  const deleteCityHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteCityByIdHandler
        .mutateAsync({ cityId: targetedCity?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('City Deleted!!');

            setShowDeleteModal(false);

            setSearchText('');
            setTargetedCity(null);
            setFilterProvince('All');
            setIsResetFilter(true);

            queryClient.invalidateQueries({ queryKey: ['city-list'] });
          } else {
            toast.error(res.data.message || 'City Deleting Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering city modal container component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditModal && (
          <EditCityModal
            city={targetedCity as City}
            handleModalClose={() => setShowEditModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this{' '}
                <span className='font-semibold text-base-light-blue'>
                  ({targetedCity?.city_name})
                </span>{' '}
                city? Once you delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteCityHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default CityModalContainer;
